import React from 'react'

import { FileUploaderUrlResponse } from '@flowwai-frontend/bps-officer-web/graphql'
import {
   AUDFileIcon,
   CADFileIcon,
   DefaultFileIcon,
   DWGFileIcon,
   File04Icon,
   File04OutlineIcon,
   HeadPhones01Icon,
   HeadPhones01OutlineIcon,
   IconPropsType,
   Image03Icon,
   Image03OutlineIcon,
   IMGFileIcon,
   PDF03Icon,
   VideoRecorderIcon,
   VideoRecorderOutlineIcon,
   VIDFileIcon,
   Jpeg02Icon,
   PDFFileTypeIcon
} from '@flowwai-frontend/design-system/icons'
import {
   AttachmentModel,
   FieldValidationReturnType
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   Tags,
   FileModel,
   FileUploadStatus,
   FileUploaderVariant
} from '@flowwai-frontend/bps-officer-web/common/data-access'

import {
   DWG_MIME_TYPE,
   SUPPORTED_FILE_UPLOADER_FORMATS
} from '../constants/FileUploaderConstants'
import { i18nType } from '../types/ToastTypes'
import { FileType } from '../types/FilePreviewCarouselTypes'

import { showAlertToastWithTitleAndDescription } from './ToastUtil'

export const isAudioFile = (mimeTye: string): boolean =>
   mimeTye.includes('audio')

export const isImageFile = (mimeTye: string): boolean =>
   mimeTye.includes('image')

export const isApplicationFile = (mimeTye: string): boolean =>
   mimeTye.includes('application')

export const isTextFile = (mimeTye: string): boolean => mimeTye.includes('text')

export const isVideoFile = (mimeTye: string): boolean =>
   mimeTye.includes('video') //TODO: need to confirm

export const isDWGFile = (mimeTye: string): boolean => mimeTye.includes('dwg')

export const isCADFile = (mimeTye: string): boolean => mimeTye.includes('cad')

export const getFeaturedIconBasedOnMimeType = (
   mimeType: string,
   variant?: FileUploaderVariant
) => {
   const sizes =
      variant === 'SLIM'
         ? { height: 20, width: 20 }
         : {
              height: 24,
              width: 24
           }

   if (isAudioFile(mimeType)) {
      return <HeadPhones01Icon {...sizes} />
   } else if (isImageFile(mimeType)) {
      return <Image03Icon {...sizes} />
   } else if (isVideoFile(mimeType)) {
      return <VideoRecorderIcon {...sizes} />
   } else if (isApplicationFile(mimeType)) {
      return <File04Icon {...sizes} />
   }
   return <File04Icon {...sizes} />
}

export function convertBytes(bytes: number): string {
   const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

   let l = 0,
      n = bytes

   while (n >= 1024 && ++l) {
      n = n / 1024
   }

   return `${n.toFixed(n < 10 && l > 0 ? 2 : 0)} ${units[l]}`
}

export const onChangeFileUploaderField = (
   selectedFile: File,
   fileUploadersMachineSend: any,
   bucketPath: string,
   commonT: i18nType
) => {
   const { name, size, type } = selectedFile

   if (size >= 40000000) {
      showAlertToastWithTitleAndDescription(
         commonT('limitExceeded'),
         commonT('selectFileLessThan40MB')
      )
      return
   }

   // spawnNewFileUploader(
   //    fileUploadersMachineSend,
   //    uuidV4(),
   //    {
   //       name,
   //       mimeType: type,
   //       sizeInBytes: size,
   //       uri: URL.createObjectURL(selectedFile)
   //    },
   //    bucketPath
   // )
}

export const convertFileToAttachmentModel = (
   file: File,
   url: string
): AttachmentModel =>
   AttachmentModel.createAttachmentModel({
      attachmentId: '-1',
      name: file.name,
      mimeType: file.type,
      sizeInBytes: file.size,
      url,
      preSignedUrl: ''
   })

export const getFileTypeFromMimeType = (mimeType: string): FileType => {
   if (isAudioFile(mimeType)) return 'AUDIO'

   if (isImageFile(mimeType)) return 'IMAGE'

   if (isVideoFile(mimeType)) return 'VIDEO'

   if (isApplicationFile(mimeType) || isTextFile(mimeType)) return 'DOCUMENT'

   throw new Error(`Invalid mimeType: ${mimeType}`)
}

export const getFileOutlineIconBasedOnMimeType = (
   mimeType: string,
   props?: IconPropsType
): React.ReactElement => {
   if (isAudioFile(mimeType)) {
      return <HeadPhones01OutlineIcon {...props} />
   } else if (isImageFile(mimeType)) {
      return <Image03OutlineIcon {...props} />
   } else if (isVideoFile(mimeType)) {
      return <VideoRecorderOutlineIcon {...props} />
   } else if (isApplicationFile(mimeType)) {
      return <File04OutlineIcon {...props} />
   }
   return <File04OutlineIcon {...props} />
}

export const getFileUploadStatus = (
   machineTags: Set<string>
): FileUploadStatus => {
   switch (true) {
      case machineTags.has(Tags.FAILURE):
         return 'FAILED'
      case machineTags.has(Tags.SUCCESS):
         return 'SUCCESS'
      case machineTags.has(Tags.IDLE):
         return 'IDLE'
      default:
         return 'UPLOADING'
   }
}

export const getConvertedFileAcceptedTypes = (fileTypes: string[]): string[] =>
   fileTypes.map(item => {
      const option = SUPPORTED_FILE_UPLOADER_FORMATS.find(
         option => option.value === item
      )
      return option?.label ?? ''
   })

export const getFileNameAndType = (
   fileNameWithType: string | undefined
): Record<string, string> => {
   if (!fileNameWithType) return { fileName: '', fileType: '' }

   const fileNameList = fileNameWithType.split('.')

   const fileName = fileNameList.slice(0, -1).join('.')

   const fileType = fileNameList.slice(-1)[0]

   return { fileName, fileType }
}

export const getReadableFileTypeFromFileAcceptTypes = (
   fileAcceptTypes: string
): string => fileAcceptTypes.replace(/[^,]+\//g, ' ').replace(/\./g, ' ')

export const convertFileUploaderUrlResponseToAttachmentModel = (
   file: FileUploaderUrlResponse
): AttachmentModel =>
   AttachmentModel.createAttachmentModel({
      attachmentId: file.fileId,
      name: file.name,
      mimeType: file.mimeType,
      sizeInBytes: file.sizeInBytes,
      url: file.url,
      preSignedUrl: ''
   })

export const validateFile = (
   fileModel: FileModel,
   maxFileSize: number,
   supportedMimeType: string,
   errorT: i18nType,
   allowAllFileTypes?: boolean
): FieldValidationReturnType => {
   const isMaxFileSizeReached = fileModel.file.size > maxFileSize
   if (isMaxFileSizeReached)
      return {
         isValid: false,
         errorMessage: errorT('fileSizeLimitExceeded')
      }

   const isValidFileType = allowAllFileTypes
      ? true
      : fileModel.file.type === supportedMimeType

   if (!isValidFileType) {
      const fileType = getReadableFileTypeFromFileAcceptTypes(supportedMimeType)
      return {
         isValid: false,
         errorMessage: errorT('invalidFileType', { fileType })
      }
   }

   return {
      isValid: true
   }
}

export const getAttachmentIconBasedOnMimeType = (
   mimeType: string,
   fileType: string,
   props?: IconPropsType
): React.ReactElement => {
   switch (true) {
      case isAudioFile(mimeType): {
         return <AUDFileIcon {...props} text={fileType} />
      }
      case isDWGFile(mimeType) || fileType.includes(DWG_MIME_TYPE): {
         return <DWGFileIcon {...props} text={fileType} />
      }
      case isCADFile(mimeType): {
         return <CADFileIcon {...props} text={fileType} />
      }
      case isImageFile(mimeType): {
         return <IMGFileIcon {...props} text={fileType} />
      }
      case isVideoFile(mimeType): {
         return <VIDFileIcon {...props} text={fileType} />
      }
      case isApplicationFile(mimeType): {
         return <PDF03Icon {...props} text={fileType} />
      }
      default: {
         return <DefaultFileIcon {...props} />
      }
   }
}

export const convertStreamToFile = async (
   stream: ReadableStream<Uint8Array>,
   fileName: string,
   mimeType: string
): Promise<File> => {
   const chunks: Uint8Array[] = []
   const reader = stream.getReader()

   let done = false
   while (!done) {
      const { value, done: readerDone } = await reader.read()
      if (value) chunks.push(value)
      done = readerDone
   }

   const blob = new Blob(chunks, { type: mimeType })
   return new File([blob], fileName, { type: mimeType })
}
